/**
 * אימות דו-שלבי — the challenge. `packages/authn/src/totp.ts`, `recovery-codes.ts`.
 *
 * ── THE FOUR ANSWERS, AND WHY THREE OF THEM READ THE SAME ───────────────────────────────────────
 *
 * `verifyTotp` distinguishes `malformed`, `incorrect` and `replayed`, and the third is the
 * interesting one: a TOTP code is valid for its whole window — ninety seconds here — so a code seen
 * over a shoulder, captured from a phishing page or read out of a log is reusable for the rest of
 * that window unless the server refuses to accept the same step twice. `lastAcceptedStep` is a
 * REQUIRED parameter for that reason; there is no overload that omits it.
 *
 * The user-facing text for `incorrect` and `replayed` is deliberately IDENTICAL. Telling somebody
 * "that code was already used" tells an attacker that the code was right, which is the one fact the
 * replay defence exists to withhold. `malformed` is different because it is the user's own typing
 * and saying so helps them; it reveals nothing.
 *
 * ── NOTHING HERE DECIDES ANYTHING ───────────────────────────────────────────────────────────────
 *
 * The code is posted; the server answers. This screen holds no secret, compares nothing, and counts
 * no attempts — rate limiting is the gate's stage, and a counter in a browser is a counter an
 * attacker owns.
 *
 * ── AND A RESET NEVER ISSUES A SESSION ──────────────────────────────────────────────────────────
 *
 * `resetGrantsSession()` returns a constant `false` on the server so that "reset does not bypass
 * MFA" is a named, tested fact rather than an absence of code. Which is why the alternative offered
 * below is a RECOVERY CODE — a second factor — and never "send me an email to get in".
 */

import { useState } from 'react';
import type { TextKey } from '../i18n/catalogue';
import { useLocale } from '../i18n/LocaleProvider';
import type { FormEvent } from 'react';
import { AppShell } from '../shell/AppShell';
import { StateBlock } from '../components/StateBlock';
import { TOTP_DIGITS } from '../api/contract';
import type { MfaChallengeOutcome } from '../api/contract';
import { callOperation } from '../api/operations';
import { routes } from '../routes';
import { resolveLandingPath } from '../shell/landing';


/**
 * `incorrect` and `replayed` are the same sentence on purpose. A different message for a reused code
 * confirms that the code was correct, which is exactly what the replay defence withholds.
 *
 * ── `factor` CHANGES THE REMEDY AND NEVER THE REASON. **§2.9, 2026-10-29.** ──────────────
 *
 * The title and the absence of any hint about WHICH failure occurred are identical on both paths,
 * which is what the uniform 401 exists for. What differs is the next step: the body said *wait for
 * the next code in the app*, and somebody who just failed a RECOVERY code is on that path precisely
 * because the app is gone. Advice they cannot take is not a gentler refusal, it is a dead end.
 */
export const challengeTextHe = (
  t: (key: TextKey) => string,
  factor: 'totp' | 'recovery_code' = 'totp',
): Readonly<Record<MfaChallengeOutcome, { readonly titleHe: string; readonly bodyHe: string }>> => ({
  accepted: {
    titleHe: t('mfa.doneTitle'),
    bodyHe: t('mfa.doneBody'),
  },
  malformed: {
    titleHe: t('mfa.badFormat'),
    bodyHe: `${t('mfa.digitsPrefix')} ${String(TOTP_DIGITS)} ${t('mfa.digitsSuffix')}`,
  },
  incorrect: {
    titleHe: t('mfa.wrongTitle'),
    bodyHe: t(factor === 'recovery_code' ? 'mfa.wrongBodyRecovery' : 'mfa.wrongBody'),
  },
  replayed: {
    titleHe: t('mfa.wrongTitle'),
    bodyHe: t(factor === 'recovery_code' ? 'mfa.wrongBodyRecovery' : 'mfa.wrongBody'),
  },
});

/**
 * THE SCREEN NOW POSTS. **Phase 2 — the defect `operations.ts` opens by naming.**
 *
 * *"`MfaChallengeScreen.tsx` exists, and its submit handler sets a local boolean; `auth.mfa.verify`
 * was certified reachable for months."* That is the exact defect the operation registry was built to
 * make impossible to repeat, and it was still true of this screen — the one on the sign-in path, so
 * an office whose policy requires a second factor could not get into the product at all.
 *
 * ── WHAT THE SCREEN IS ALLOWED TO KNOW ─────────────────────────────────────────────────────────
 *
 * Nothing. Every failure — unknown challenge, expired challenge, wrong code, REPLAYED code, spent
 * recovery code — is the same 401, deliberately, so the endpoint cannot be used to learn which was
 * true. So this screen renders ONE refusal for all of them and does not try to be helpful about
 * which; a screen that guessed would be reconstructing the distinction the endpoint removes.
 *
 * The challenge reference comes from the ADDRESS, because sign-in put it there: the principal holds
 * no cookie yet, so there is no session to carry it and nowhere else it could live. A missing one
 * is a person who reached this screen without signing in, and the screen says so rather than posting
 * an empty reference and reading the generic refusal back.
 *
 * It arrives as a PROP, read by `<MfaChallengeRoute>` in `App.tsx` — the same shape as every other
 * parameterised screen here. A screen that called `useSearchParams` itself would need a `<Router>`
 * around it to render at all, which makes the router a dependency of testing the second factor.
 */
export interface MfaChallengeScreenProps {
  /** From `?challenge=` — `null` when somebody reached this address without signing in. */
  readonly challengeRef: string | null;
}

export function MfaChallengeScreen({ challengeRef }: MfaChallengeScreenProps): JSX.Element {
  const { t } = useLocale();
  const [code, setCode] = useState('');
  const [useRecovery, setUseRecovery] = useState(false);
  const [sending, setSending] = useState(false);
  const [outcome, setOutcome] = useState<'verified' | 'refused' | null>(null);

  function submit(e: FormEvent): void {
    e.preventDefault();
    if (challengeRef === null || sending) return;
    setSending(true);
    setOutcome(null);
    void callOperation('auth.mfa.verify', {
      // `factor` states which one is being presented. See the declared body for why it is not
      // inferred from the code.
      body: { challengeRef, code, factor: useRecovery ? 'recovery_code' : 'totp' },
    })
      .then(
        () => {
          setOutcome('verified');
          /*
           * THE SESSION EXISTS NOW, SO ASK WHAT IT IS.
           *
           * This line said `routes.companies`, and a Platform Owner who had just completed
           * password AND second factor was delivered into the tenant workspace -- the one surface
           * their account holds nothing on. The destination comes from `resolveLandingPath`, which
           * reads `/api/me`; the MECHANISM stays a full page load, deliberately, because
           * everything downstream re-reads the session from the server and starting the app again
           * is the simplest way to be sure nothing stale survives the cookie changing.
           *
           * A failed resolve goes to the ROOT rather than to a guessed surface: `/` applies the
           * same rule from the same answer, so the fallback is the rule again and not an exception
           * to it.
           */
          void resolveLandingPath().then(
            (path) => window.location.assign(path),
            () => window.location.assign('/'),
          );
        },
        () => {
          // One refusal for every reason. See this screen's header.
          setOutcome('refused');
        },
      )
      .finally(() => setSending(false));
  }

  return (
    <AppShell screenId="mfa-challenge"
      /* Every string here goes through `t()`; `translatedScreens.test.ts` fails the build
         if one Hebrew literal survives, which is what makes this a claim and not a note. */
      translated
      titleHe={t('personal.mfa')}
    >
      <p className="screen-lead">
        {useRecovery
          ? t('mfa.recoveryHint')
          : t('mfa.codeHint')}
      </p>

      <form className="form" onSubmit={submit}>
        <label className="field">
          <span className="field__label">{useRecovery ? t('mfa.recoveryLabel') : t('mfa.codeLabel')}</span>
          <input
            className="field__input"
            dir="ltr"
            inputMode={useRecovery ? 'text' : 'numeric'}
            autoComplete="one-time-code"
            {...(useRecovery ? {} : { maxLength: TOTP_DIGITS })}
            value={code}
            onChange={(e) => setCode(e.currentTarget.value)}
            required
          />
        </label>

        <div className="actions-row">
          <button type="submit" className="btn btn--primary" disabled={sending || challengeRef === null}>
            {sending ? t('common.checking') : t('mfa.verify')}
          </button>
          <button
            type="button"
            className="btn btn--quiet"
            onClick={() => {
              setUseRecovery(!useRecovery);
              setCode('');
              setOutcome(null);
            }}
          >
            {useRecovery ? t('mfa.backToCode') : t('mfa.useRecovery')}
          </button>
        </div>
      </form>

      {challengeRef === null ? (
        <StateBlock
          titleHe={t('mfa.noChallengeTitle')}
          // Not "your code is wrong" — nothing was checked. The person reached this address without
          // a sign-in that issued a challenge, and the only thing to do is start again.
          bodyHe={t('mfa.noChallengeBody')}
          tone="unavailable"
          nextStep={
            <a className="btn btn--nav" href={routes.signIn}>
              {t('personal.goToSignIn')}
            </a>
          }
        />
      ) : null}

      {outcome === 'refused' ? (
        <StateBlock
          titleHe={challengeTextHe(t).incorrect.titleHe}
          bodyHe={challengeTextHe(t, useRecovery ? 'recovery_code' : 'totp').incorrect.bodyHe}
          tone="error"
          /*
           * AND THE NEXT-STEP LINK FOLLOWS THE SAME RULE. "Back to the setup screen" is the right
           * offer to somebody whose authenticator works and whose code was simply wrong; it is the
           * WRONG offer to somebody mid-recovery, because enrolling again needs a factor they
           * cannot present. On that path there is no next step but the field they are already in.
           */
          nextStep={
            useRecovery ? null : (
              <a className="btn" href={routes.mfaEnrolment}>
                {t('mfa.backToEnrol')}
              </a>
            )
          }
        />
      ) : null}

      {/*
        ── THE STANDING PARAGRAPH IS GONE. **Owner, section 7, 2026-11-01.** ────────────────────

            *"הסר ממנו את הפסקה הקבועה על כך שאיפוס סיסמה אינו עוקף אימות — היא אינה מסייעת
            לפעולה."*

        It said that a password reset does not waive the second factor. That is TRUE, it is
        enforced on the server (`resetGrantsSession()` returns a constant `false`, and the note at
        the head of this file records why), and it is of no use to the person standing at this
        screen with a code to type. It answered a question nobody on this screen is asking, every
        single time, in a box the size of the thing they came here to do.

        NOTHING ABOUT THE RULE CHANGED. The rule lives on the server; what left is a paragraph
        about it on a screen that cannot act on it.
      */}
    </AppShell>
  );
}
